import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Trash2, LogOut, FileText, Users, FolderOpen, PenTool, ClipboardList, Award, Mail, Music, Mic, Package, KeyRound, Presentation, BarChart3 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "gls_admin_token";
const authHeaders = (token) => ({ headers: { "X-Admin-Token": token } });

const KIND_LABEL = { ebook: "Ebook", workbook: "Workbook", supplement: "Supplement" };

export default function AdminUploads() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("files");
  const [products, setProducts] = useState([]);
  const [files, setFiles] = useState([]);
  const [signups, setSignups] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedKind, setSelectedKind] = useState("ebook");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [signature, setSignature] = useState(null);
  const [sigFile, setSigFile] = useState(null);
  const [sigUploading, setSigUploading] = useState(false);
  const [quizzes, setQuizzes] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [quizProductId, setQuizProductId] = useState("");
  const [quizJson, setQuizJson] = useState("");
  const [quizSaving, setQuizSaving] = useState(false);
  const [drips, setDrips] = useState([]);
  // ---- Access codes ----
  const [codes, setCodes] = useState([]);
  const [newCode, setNewCode] = useState("");
  const [newCodeProducts, setNewCodeProducts] = useState([]);
  const [newCodeMaxUses, setNewCodeMaxUses] = useState("");
  const [newCodeNote, setNewCodeNote] = useState("");
  const [newCodeBusy, setNewCodeBusy] = useState(false);

  // ---- Course settings (per-course video embed URL) ----
  const [courseSettings, setCourseSettings] = useState({}); // {product_id: {video_embed_url}}
  const [videoUrl, setVideoUrl] = useState("");
  const [videoSaving, setVideoSaving] = useState(false);
  // ---- Course page content ----
  const [pageNarrative, setPageNarrative] = useState("");
  const [pageDescription, setPageDescription] = useState("");
  const [pageObjectives, setPageObjectives] = useState("");
  const [pageWhyTake, setPageWhyTake] = useState("");
  const [pageWhoFor, setPageWhoFor] = useState("");
  const [pageWhyChoose, setPageWhyChoose] = useState("");
  const [pageLengthHours, setPageLengthHours] = useState("");
  const [pageSaving, setPageSaving] = useState(false);
  // ---- Course modules ----
  const [modules, setModules] = useState([]);
  const [newModuleTitle, setNewModuleTitle] = useState("");
  const [uploadingModuleId, setUploadingModuleId] = useState(null);

  // ---- ElevenLabs TTS state ----
  const [voices, setVoices] = useState([]);
  const [defaultVoiceId, setDefaultVoiceId] = useState("");
  const [ttsProductId, setTtsProductId] = useState("");
  const [ttsText, setTtsText] = useState("");
  const [ttsVoiceId, setTtsVoiceId] = useState("");
  const [ttsBusy, setTtsBusy] = useState(false);
  // Voice cloning
  const [cloneName, setCloneName] = useState("");
  const [cloneDesc, setCloneDesc] = useState("");
  const [cloneFiles, setCloneFiles] = useState([]);
  const [cloneBusy, setCloneBusy] = useState(false);

  // ---- Slide-Deck Batch Narrator ----
  const [batchDeckName, setBatchDeckName] = useState("");
  const [batchVoiceId, setBatchVoiceId] = useState("");
  const [batchText, setBatchText] = useState("");
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchLastResult, setBatchLastResult] = useState(null); // {slides, seconds}

  // ---- /character enrollment funnel analytics ----
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  // ---- Admin runtime settings (MEGA sync toggle) ----
  const [adminSettings, setAdminSettings] = useState(null);

  useEffect(() => { if (token) verifyToken(token); }, []);
  // Analytics tab loader — placed above the early "!authed" return so hook order stays stable
  useEffect(() => { if (tab === "analytics" && token) { loadAnalytics(); loadAdminSettings(); } }, [tab, token]);

  const verifyToken = async (t) => {
    try {
      await axios.post(`${API}/admin/verify`, {}, authHeaders(t));
      localStorage.setItem(TOKEN_KEY, t);
      setAuthed(true);
      loadAll(t);
    } catch {
      toast.error("Invalid admin token");
      localStorage.removeItem(TOKEN_KEY);
      setAuthed(false);
    }
  };

  const loadAll = async (t) => {
    const [p, f, n, s, q, cc, dr, vv, cs, ac] = await Promise.all([
      axios.get(`${API}/products`),
      axios.get(`${API}/admin/files`, authHeaders(t)),
      axios.get(`${API}/admin/newsletter`, authHeaders(t)),
      axios.get(`${API}/admin/signature`, authHeaders(t)),
      axios.get(`${API}/admin/quizzes`, authHeaders(t)),
      axios.get(`${API}/admin/completions`, authHeaders(t)),
      axios.get(`${API}/admin/drips`, authHeaders(t)),
      axios.get(`${API}/admin/tts/voices`, authHeaders(t)).catch(() => ({ data: { voices: [], default_voice_id: "" } })),
      axios.get(`${API}/admin/courses/settings`, authHeaders(t)).catch(() => ({ data: { settings: [] } })),
      axios.get(`${API}/admin/codes`, authHeaders(t)).catch(() => ({ data: { codes: [] } })),
    ]);
    setProducts(p.data.products);
    setFiles(f.data.files);
    setSignups(n.data.signups);
    setSignature(s.data.signature);
    setQuizzes(q.data.quizzes);
    setCompletions(cc.data.completions);
    setDrips(dr.data.drips);
    setVoices(vv.data.voices || []);
    setDefaultVoiceId(vv.data.default_voice_id || "");
    const csMap = {};
    (cs.data.settings || []).forEach((row) => { csMap[row.product_id] = row; });
    setCourseSettings(csMap);
    setCodes(ac.data.codes || []);
    if (!ttsVoiceId && vv.data.default_voice_id) setTtsVoiceId(vv.data.default_voice_id);
    if (!selectedProduct && p.data.products.length) setSelectedProduct(p.data.products[0].id);
    if (!ttsProductId && p.data.products.length) setTtsProductId(p.data.products[0].id);
  };

  const login = (e) => { e.preventDefault(); verifyToken(token.trim()); };
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(""); setAuthed(false); setFiles([]); setSignups([]); setProducts([]);
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Pick a PDF first");
    if (file.type !== "application/pdf") return toast.error("PDF files only");
    if (file.size > 25 * 1024 * 1024) return toast.error("Max 25 MB");
    const fd = new FormData();
    fd.append("product_id", selectedProduct);
    fd.append("kind", selectedKind);
    fd.append("file", file);
    setUploading(true);
    try {
      await axios.post(`${API}/admin/upload`, fd, {
        headers: { "X-Admin-Token": token, "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded → ${selectedProduct} / ${selectedKind}`);
      setFile(null);
      document.getElementById("admin-file-input").value = "";
      loadAll(token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const detach = async (id) => {
    if (!window.confirm("Detach this file from its product?")) return;
    try {
      await axios.delete(`${API}/admin/files/${id}`, authHeaders(token));
      toast.success("Detached");
      loadAll(token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const attachedList = (productId, kind) =>
    files.filter((f) => f.product_id === productId && f.kind === kind);

  const uploadSignature = async (e) => {
    e.preventDefault();
    if (!sigFile) return toast.error("Pick a signature image first");
    if (!["image/png", "image/jpeg", "image/jpg"].includes(sigFile.type))
      return toast.error("PNG or JPG only");
    if (sigFile.size > 5 * 1024 * 1024) return toast.error("Max 5 MB");
    const fd = new FormData();
    fd.append("file", sigFile);
    setSigUploading(true);
    try {
      await axios.post(`${API}/admin/signature`, fd, {
        headers: { "X-Admin-Token": token, "Content-Type": "multipart/form-data" },
      });
      toast.success("Signature installed on all future certificates");
      setSigFile(null);
      const inp = document.getElementById("admin-sig-input");
      if (inp) inp.value = "";
      loadAll(token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signature upload failed");
    } finally { setSigUploading(false); }
  };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-4 py-24">
        <div className="text-[10px] tracking-[0.32em] uppercase text-[#8C4A57] mb-3">Admin</div>
        <h1 className="text-3xl font-serif font-semibold text-[#2D2628] mb-6">Sign in</h1>
        <form onSubmit={login} className="space-y-3" data-testid="admin-login-form">
          <input data-testid="admin-token-input" type="password" value={token}
            onChange={(e) => setToken(e.target.value)} placeholder="Admin token"
            className="w-full border border-[#E8DCDD] rounded-sm px-4 py-3 focus:outline-none focus:border-[#8C4A57]" />
          <button data-testid="admin-login-btn" type="submit" className="btn-primary w-full justify-center">
            Continue
          </button>
        </form>
      </div>
    );
  }

  const audioAttachments = files.filter((f) => f.kind === "audio");

  const createCode = async () => {
    if (!newCode.trim()) return toast.error("Enter a code");
    if (!newCodeProducts.length) return toast.error("Pick at least one product");
    setNewCodeBusy(true);
    try {
      await axios.post(`${API}/admin/codes`, {
        code: newCode.trim().toUpperCase(),
        product_ids: newCodeProducts,
        max_uses: newCodeMaxUses ? parseInt(newCodeMaxUses) : null,
        note: newCodeNote.trim() || null,
      }, authHeaders(token));
      toast.success("Code created");
      setNewCode(""); setNewCodeProducts([]); setNewCodeMaxUses(""); setNewCodeNote("");
      await loadAll(token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create code");
    } finally { setNewCodeBusy(false); }
  };

  const revokeCode = async (code) => {
    if (!window.confirm(`Revoke code "${code}"? It won't work for any future redemptions.`)) return;
    try {
      await axios.delete(`${API}/admin/codes/${code}`, authHeaders(token));
      toast.success("Code revoked");
      await loadAll(token);
    } catch (err) { toast.error(err.response?.data?.detail || "Revoke failed"); }
  };

  // ---- Module handlers ----
  const reloadModules = async () => {
    if (!quizProductId) return;
    const r = await axios.get(`${API}/admin/modules?course_id=${quizProductId}`, authHeaders(token));
    setModules(r.data.modules || []);
  };
  const createModule = async () => {
    if (!quizProductId) return toast.error("Pick a course first");
    if (!newModuleTitle.trim()) return toast.error("Enter a module title");
    try {
      await axios.post(`${API}/admin/modules`,
        { course_id: quizProductId, title: newModuleTitle.trim() }, authHeaders(token));
      toast.success("Module added"); setNewModuleTitle(""); await reloadModules();
    } catch (err) { toast.error(err.response?.data?.detail || "Add failed"); }
  };
  const updateModule = async (m, patch) => {
    try {
      await axios.put(`${API}/admin/modules/${m.id}`, patch, authHeaders(token));
      await reloadModules();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };
  const deleteModule = async (m) => {
    if (!window.confirm(`Delete module "${m.title}"? Attached files stay in the course but are detached from this module.`)) return;
    try {
      await axios.delete(`${API}/admin/modules/${m.id}`, authHeaders(token));
      toast.success("Module deleted"); await reloadModules();
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };
  const moveModule = async (idx, delta) => {
    const next = [...modules];
    const j = idx + delta;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setModules(next);
    try {
      await axios.put(`${API}/admin/modules/reorder`,
        { course_id: quizProductId, ordered_ids: next.map(m => m.id) }, authHeaders(token));
    } catch (err) { toast.error("Reorder failed"); await reloadModules(); }
  };
  const uploadToModule = async (moduleId, kind, file) => {
    if (!file) return;
    setUploadingModuleId(moduleId);
    try {
      const fd = new FormData();
      fd.append("product_id", quizProductId);
      fd.append("kind", kind);
      fd.append("module_id", moduleId);
      fd.append("file", file);
      await axios.post(`${API}/admin/upload`, fd, {
        headers: { "X-Admin-Token": token, "Content-Type": "multipart/form-data" },
      });
      toast.success("File attached to module");
      await reloadModules();
      await loadAll(token);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploadingModuleId(null); }
  };
  const bundleProducts = products.filter((p) => p.is_bundle && Array.isArray(p.course_ids) && p.course_ids.length);
  const bundleReadiness = bundleProducts.map((b) => {
    const members = b.course_ids.map((pid) => {
      const mp = products.find((p) => p.id === pid);
      const wb = files.find((f) => f.product_id === pid && f.kind === "workbook");
      const videoSet = !!courseSettings[pid]?.video_embed_url;
      return {
        product_id: pid,
        title: mp?.title || pid,
        missing: !mp,
        has_workbook: !!wb,
        workbook_filename: wb?.original_filename,
        has_video: videoSet,
      };
    });
    const missing = members.filter((m) => !m.has_workbook || !m.has_video).length;
    return { bundle: b, members, missing_count: missing };
  });

  const tabs = [
    { id: "files",       label: "Files",              icon: FolderOpen,    count: files.length },
    { id: "bundles",     label: "Bundle Readiness",   icon: Package,       count: bundleReadiness.reduce((n, b) => n + b.missing_count, 0) },
    { id: "codes",       label: "Access Codes",       icon: KeyRound,      count: codes.filter(c => !c.is_revoked).length },
    { id: "audio",       label: "Audio (TTS)",        icon: Music,         count: audioAttachments.length },
    { id: "narrator",    label: "Slide Narrator",     icon: Presentation,  count: 0 },
    { id: "analytics",   label: "Enrollment Funnel",  icon: BarChart3,     count: 0 },
    { id: "certificate", label: "Certificate",        icon: PenTool,       count: signature ? 1 : 0 },
    { id: "quizzes",     label: "Quizzes",            icon: ClipboardList, count: quizzes.length },
    { id: "completions", label: "Completions",        icon: Award,         count: completions.length },
    { id: "drips",       label: "Email Drips",        icon: Mail,          count: drips.filter(d => !d.sent && !d.cancelled).length },
    { id: "newsletter",  label: "Newsletter Signups", icon: Users,         count: signups.length },
  ];

  const courseProducts = products.filter(p => p.type === "course");

  const loadQuizForProduct = (pid) => {
    setQuizProductId(pid);
    setVideoUrl(courseSettings[pid]?.video_embed_url || "");
    // Load course page content
    axios.get(`${API}/courses/${pid}/settings`).then(r => {
      const s = r.data || {};
      setPageNarrative(s.narrative || "");
      setPageDescription(s.description || "");
      setPageObjectives((s.learning_objectives || []).join("\n"));
      setPageWhyTake(s.why_take || "");
      setPageWhoFor((s.who_for || []).join("\n"));
      setPageWhyChoose((s.why_choose || []).join("\n"));
      setPageLengthHours(s.length_hours != null ? String(s.length_hours) : "");
    }).catch(() => {});
    // Load modules
    axios.get(`${API}/admin/modules?course_id=${pid}`, authHeaders(token))
      .then(r => setModules(r.data.modules || []))
      .catch(() => setModules([]));
    const existing = quizzes.find(q => q.product_id === pid);
    if (existing) {
      const clean = { product_id: existing.product_id, title: existing.title,
                       passing_score: existing.passing_score, questions: existing.questions };
      setQuizJson(JSON.stringify(clean, null, 2));
    } else {
      setQuizJson(JSON.stringify({
        product_id: pid, title: "Course Assessment", passing_score: 70,
        questions: [
          { id: "q1", type: "multiple_choice",
            prompt: "Sample multiple choice question?",
            options: ["Option A", "Option B", "Option C", "Option D"],
            correct_answer: "0", points: 1 },
          { id: "q2", type: "short_answer",
            prompt: "Sample short answer question?",
            correct_answer: "keyword1|keyword2", points: 1 },
        ],
      }, null, 2));
    }
  };

  const saveQuiz = async () => {
    if (!quizProductId) return toast.error("Pick a course first");
    let payload;
    try { payload = JSON.parse(quizJson); }
    catch { return toast.error("Invalid JSON — please fix formatting"); }
    if (payload.product_id !== quizProductId) {
      return toast.error(`product_id in the JSON must be "${quizProductId}"`);
    }
    setQuizSaving(true);
    try {
      await axios.put(`${API}/admin/quizzes/${quizProductId}`, payload, authHeaders(token));
      toast.success("Quiz saved");
      await loadAll(token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save quiz");
    } finally { setQuizSaving(false); }
  };

  const deleteQuiz = async (pid) => {
    if (!window.confirm(`Delete the quiz for ${pid}?`)) return;
    try {
      await axios.delete(`${API}/admin/quizzes/${pid}`, authHeaders(token));
      toast.success("Quiz deleted");
      if (quizProductId === pid) { setQuizProductId(""); setQuizJson(""); }
      await loadAll(token);
    } catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  // ---- ElevenLabs TTS handlers ----
  const generateTTS = async () => {
    if (!ttsProductId) return toast.error("Pick a product");
    if (!ttsText.trim()) return toast.error("Enter the text to narrate");
    if (ttsText.length > 10000) return toast.error("Text must be under 10,000 characters");
    const chosen = voices.find(v => v.voice_id === ttsVoiceId);
    setTtsBusy(true);
    try {
      await axios.post(`${API}/admin/tts/generate`, {
        product_id: ttsProductId,
        text: ttsText,
        voice_id: ttsVoiceId || undefined,
        voice_name: chosen?.name || undefined,
      }, authHeaders(token));
      toast.success(`Audio generated & attached to ${ttsProductId}`);
      setTtsText("");
      await loadAll(token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "TTS generation failed");
    } finally { setTtsBusy(false); }
  };

  const submitClone = async (e) => {
    e.preventDefault();
    if (!cloneName.trim()) return toast.error("Give your voice a name");
    if (!cloneFiles.length) return toast.error("Upload at least one voice sample");
    const fd = new FormData();
    fd.append("voice_name", cloneName.trim());
    if (cloneDesc.trim()) fd.append("description", cloneDesc.trim());
    Array.from(cloneFiles).forEach((f) => fd.append("files", f));
    setCloneBusy(true);
    try {
      const resp = await axios.post(`${API}/admin/tts/clone`, fd, {
        headers: { "X-Admin-Token": token, "Content-Type": "multipart/form-data" },
      });
      toast.success(`Voice cloned — ID ${resp.data.voice_id}`);
      setCloneName(""); setCloneDesc(""); setCloneFiles([]);
      const inp = document.getElementById("clone-file-input");
      if (inp) inp.value = "";
      await loadAll(token);
      if (resp.data.voice_id) setTtsVoiceId(resp.data.voice_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Voice cloning failed");
    } finally { setCloneBusy(false); }
  };

  // ---- Slide-Deck Batch Narrator ----
  // Parse a raw textarea into slides. Delimiter: a line of "---" between slides.
  // Within a slide, an optional first line starting with "# " becomes the title.
  const parseSlides = (raw) => {
    const blocks = raw.split(/\n\s*---+\s*\n/).map(b => b.trim()).filter(Boolean);
    return blocks.map((block, idx) => {
      const lines = block.split(/\n/);
      let title = null;
      if (lines[0] && /^#\s+/.test(lines[0])) {
        title = lines.shift().replace(/^#\s+/, "").trim();
      }
      return { slide_number: idx + 1, title, text: lines.join("\n").trim() };
    }).filter(s => s.text);
  };

  const runBatchNarration = async () => {
    const slides = parseSlides(batchText);
    if (slides.length === 0) return toast.error("Add at least one slide of notes (separate slides with a line of `---`).");
    if (slides.length > 200) return toast.error("Maximum 200 slides per batch.");
    setBatchBusy(true);
    setBatchLastResult(null);
    try {
      const resp = await axios.post(
        `${API}/admin/tts/batch`,
        {
          slides,
          voice_id: batchVoiceId || undefined,
          voice_name: voices.find(v => v.voice_id === batchVoiceId)?.name,
          deck_name: batchDeckName.trim() || "slide-deck",
        },
        { headers: { "X-Admin-Token": token }, responseType: "blob" },
      );
      // Trigger browser download of ZIP
      const filename = (batchDeckName.trim() || "slide-deck") + "-narration.zip";
      const blob = new Blob([resp.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      const seconds = parseFloat(resp.headers["x-total-duration-seconds"] || "0");
      const total = parseInt(resp.headers["x-total-slides"] || `${slides.length}`, 10);
      setBatchLastResult({ slides: total, seconds });
      toast.success(`Generated ${total} MP3s (${seconds.toFixed(1)}s total)`);
    } catch (err) {
      // Blob responses need explicit text() to read error detail
      let msg = "Batch narration failed";
      if (err.response?.data && err.response.data instanceof Blob) {
        try { msg = JSON.parse(await err.response.data.text()).detail || msg; } catch {}
      } else if (err.response?.data?.detail) {
        msg = err.response.data.detail;
      }
      toast.error(msg);
    } finally { setBatchBusy(false); }
  };

  // ---- Enrollment funnel analytics ----
  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const r = await axios.get(`${API}/admin/analytics/character-funnel`, authHeaders(token));
      setAnalytics(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load analytics");
    } finally { setAnalyticsLoading(false); }
  };

  // ---- Admin settings (MEGA sync toggle) ----
  const loadAdminSettings = async () => {
    try {
      const r = await axios.get(`${API}/admin/settings`, authHeaders(token));
      setAdminSettings(r.data);
    } catch (err) {
      // Settings endpoint may 401 if token invalid; tab will simply hide the toggle
      setAdminSettings(null);
    }
  };
  const toggleMegaSync = async () => {
    if (!adminSettings) return;
    const next = !adminSettings.mega_sync_enabled;
    try {
      await axios.patch(`${API}/admin/settings`, { mega_sync_enabled: next }, authHeaders(token));
      setAdminSettings({ ...adminSettings, mega_sync_enabled: next });
      toast.success(`MEGA sync ${next ? "enabled" : "paused"}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update setting");
    }
  };

  // ---- Course video URL save ----
  const saveVideoUrl = async () => {
    if (!quizProductId) return toast.error("Pick a course first");
    setVideoSaving(true);
    try {
      const resp = await axios.put(
        `${API}/admin/courses/${quizProductId}/settings`,
        { video_embed_url: videoUrl.trim() || null },
        authHeaders(token),
      );
      toast.success(videoUrl.trim() ? "Video URL saved" : "Video URL cleared");
      setCourseSettings((prev) => ({ ...prev, [quizProductId]: resp.data }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save video URL");
    } finally { setVideoSaving(false); }
  };

  const savePageContent = async () => {
    if (!quizProductId) return toast.error("Pick a course first");
    setPageSaving(true);
    try {
      const splitLines = s => s.split("\n").map(x => x.trim()).filter(Boolean);
      await axios.put(`${API}/admin/courses/${quizProductId}/settings`, {
        narrative: pageNarrative.trim() || null,
        description: pageDescription.trim() || null,
        learning_objectives: splitLines(pageObjectives),
        why_take: pageWhyTake.trim() || null,
        who_for: splitLines(pageWhoFor),
        why_choose: splitLines(pageWhyChoose),
        length_hours: pageLengthHours ? parseFloat(pageLengthHours) : null,
      }, authHeaders(token));
      toast.success("Course page content saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save page content");
    } finally { setPageSaving(false); }
  };


  return (
    <div data-testid="admin-uploads-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#8C4A57] mb-2">Admin</div>
          <h1 className="text-3xl sm:text-4xl font-serif font-semibold text-[#2D2628]">Product downloads</h1>
        </div>
        <button data-testid="admin-logout-btn" onClick={logout} className="btn-outline text-xs">
          <LogOut className="w-3 h-3 mr-2" /> Sign out
        </button>
      </div>

      {/* Tab strip */}
      <div className="flex gap-1 mb-8 border-b border-[#E8DCDD]" data-testid="admin-tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-5 py-3 text-xs uppercase tracking-widest -mb-px border-b-2 transition-colors ${
              tab === t.id
                ? "border-[#8C4A57] text-[#8C4A57] font-semibold"
                : "border-transparent text-[#6B5E62] hover:text-[#8C4A57]"
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
            <span className="text-[10px] bg-[#FAF4F5] px-2 py-0.5 rounded-full">{t.count}</span>
          </button>
        ))}
      </div>

      {tab === "files" && (
        <>
          <p className="text-sm text-[#6B5E62] mb-6">
            Attach an ebook PDF, workbook PDF, and up to any number of supplemental readings to each product.
            Bundle products can carry multiple ebook/workbook files. Files gated by successful Stripe payment.
          </p>

          {/* Upload form */}
          <form onSubmit={upload} data-testid="admin-upload-form"
            className="bg-[#FAF4F5] border border-[#E8DCDD] rounded-sm p-6 sm:p-8 mb-10 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <label className="text-[10px] tracking-[0.24em] uppercase text-[#6B5E62] mb-1 block">Product</label>
              <select data-testid="admin-product-select" value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
                className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 bg-white">
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    [{p.type}] {p.title.slice(0, 65)}{p.title.length > 65 ? "…" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.24em] uppercase text-[#6B5E62] mb-1 block">Kind</label>
              <select data-testid="admin-kind-select" value={selectedKind}
                onChange={(e) => setSelectedKind(e.target.value)}
                className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 bg-white">
                <option value="ebook">Ebook</option>
                <option value="workbook">Workbook</option>
                <option value="supplement">Supplement</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.24em] uppercase text-[#6B5E62] mb-1 block">PDF (max 25 MB)</label>
              <input id="admin-file-input" data-testid="admin-file-input" type="file" accept="application/pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-full text-sm" />
            </div>
            <div className="md:col-span-4">
              <button data-testid="admin-upload-submit" type="submit" disabled={uploading} className="btn-primary">
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? "Uploading…" : "Upload & attach"}
              </button>
            </div>
          </form>

          {/* Product matrix */}
          <div className="border border-[#E8DCDD] rounded-sm overflow-hidden" data-testid="admin-file-table">
            <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 bg-[#FAF4F5] text-[10px] tracking-[0.24em] uppercase text-[#6B5E62]">
              <div className="col-span-5">Product</div>
              <div className="col-span-2">Ebook</div>
              <div className="col-span-2">Workbook</div>
              <div className="col-span-3">Supplements</div>
            </div>
            {products.map((p) => {
              const eList = attachedList(p.id, "ebook");
              const wList = attachedList(p.id, "workbook");
              const sList = attachedList(p.id, "supplement");
              return (
                <div key={p.id} className="grid grid-cols-1 md:grid-cols-12 gap-4 px-4 py-4 border-t border-[#F1E7E9] items-start">
                  <div className="col-span-5">
                    <div className="text-[10px] tracking-[0.20em] uppercase text-[#8C4A57]">
                      {p.category_label}{p.is_bundle ? " · Bundle" : ""}
                    </div>
                    <div className="text-[#2D2628] font-medium leading-snug">{p.title}</div>
                    <div className="text-xs text-[#6B5E62] mt-1">{p.id}</div>
                  </div>
                  {[
                    { list: eList, kind: "ebook", cols: 2, multi: p.is_bundle },
                    { list: wList, kind: "workbook", cols: 2, multi: p.is_bundle },
                    { list: sList, kind: "supplement", cols: 3, multi: true },
                  ].map(({ list, kind, cols, multi }) => (
                    <div key={kind} className={`col-span-${cols} space-y-2`}>
                      {list.length === 0 ? (
                        <span className="text-xs text-[#B79CA1] italic">— none —</span>
                      ) : (
                        list.map((att) => (
                          <div key={att.id} className="flex items-start gap-2">
                            <FileText className="w-4 h-4 text-[#8C4A57] mt-0.5 shrink-0" />
                            <div className="min-w-0 flex-1">
                              <div className="text-xs text-[#2D2628] truncate" title={att.original_filename}>
                                {att.original_filename}
                              </div>
                              <div className="text-[10px] text-[#6B5E62]">
                                {(att.size / 1024).toFixed(0)} KB · {new Date(att.uploaded_at).toLocaleDateString()}
                              </div>
                              <button data-testid={`detach-${att.id}`} onClick={() => detach(att.id)}
                                className="text-[10px] uppercase tracking-widest text-[#8C4A57] hover:underline mt-1 inline-flex items-center gap-1">
                                <Trash2 className="w-3 h-3" /> Detach
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                      {multi && list.length > 0 && (
                        <div className="text-[10px] text-[#8C4A57] uppercase tracking-widest pt-1">
                          {list.length} attached — add more above
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </>
      )}

      {tab === "bundles" && (
        <div data-testid="admin-bundles-tab">
          <p className="text-sm text-[#6B5E62] mb-6 max-w-3xl">
            Bundles inherit their content from the individual member courses. Attach a
            workbook (via <strong>Files</strong> tab) and set a video URL (via <strong>Quizzes</strong> tab)
            for each member course — this tab shows what&apos;s still missing.
          </p>

          {bundleReadiness.length === 0 && (
            <div className="text-sm text-[#6B5E62] italic border border-dashed border-[#E8DCDD] rounded-sm p-8 text-center">
              No bundles configured yet. Bundles need a <code>course_ids</code> array in <code>products_data.py</code>.
            </div>
          )}

          <div className="space-y-8">
            {bundleReadiness.map(({ bundle, members, missing_count }) => (
              <section
                key={bundle.id}
                data-testid={`bundle-readiness-${bundle.id}`}
                className="border border-[#E8DCDD] rounded-sm overflow-hidden bg-white"
              >
                <header className="px-5 py-4 bg-[#FAF4F5] border-b border-[#E8DCDD] flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] mb-1">
                      {bundle.category_label} · Bundle
                    </div>
                    <h3 className="text-sm font-semibold text-[#2D2628]">{bundle.title}</h3>
                    <div className="text-[10px] text-[#6B5E62] mt-1">
                      {bundle.id} · {members.length} member course{members.length === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className={`text-[10px] tracking-[0.14em] uppercase px-3 py-1 rounded-sm border ${
                    missing_count === 0
                      ? "bg-[#F0F7EE] border-[#B8D8B0] text-[#3C7A2B]"
                      : "bg-[#FCF3E8] border-[#E8C899] text-[#8B5A1D]"
                  }`}>
                    {missing_count === 0 ? "Ready" : `${missing_count} missing`}
                  </div>
                </header>

                <table className="w-full text-sm">
                  <thead className="text-[10px] tracking-[0.14em] uppercase text-[#8C4A57] border-b border-[#E8DCDD]">
                    <tr>
                      <th className="text-left px-5 py-3">Course</th>
                      <th className="text-left px-5 py-3 w-[210px]">Workbook</th>
                      <th className="text-left px-5 py-3 w-[190px]">Video URL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((m) => (
                      <tr key={m.product_id} className="border-b border-[#E8DCDD] last:border-b-0" data-testid={`bundle-member-${bundle.id}-${m.product_id}`}>
                        <td className="px-5 py-3">
                          {m.missing ? (
                            <span className="text-[#B4573F]">⚠ Not in catalog: {m.product_id}</span>
                          ) : (
                            <>
                              <div className="text-[#2D2628] text-xs leading-tight">{m.title}</div>
                              <div className="text-[10px] text-[#6B5E62]">{m.product_id}</div>
                            </>
                          )}
                        </td>
                        <td className="px-5 py-3">
                          {m.has_workbook ? (
                            <span className="text-[11px] text-[#3C7A2B]" title={m.workbook_filename}>
                              ✓ Attached
                            </span>
                          ) : (
                            <span className="text-[11px] text-[#B4573F]">✗ Missing — upload in Files tab</span>
                          )}
                        </td>
                        <td className="px-5 py-3">
                          {m.has_video ? (
                            <span className="text-[11px] text-[#3C7A2B]">✓ Set</span>
                          ) : (
                            <span className="text-[11px] text-[#B4573F]">✗ Missing — set in Quizzes tab</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            ))}
          </div>
        </div>
      )}

      {tab === "codes" && (
        <div data-testid="admin-codes-tab">
          <p className="text-sm text-[#6B5E62] mb-6 max-w-3xl">
            Create access codes that unlock one or more courses or bundles when a student
            enters them at <code>/redeem</code>. Codes are case-insensitive, one redemption per account.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-[#FAF4F5] border border-[#E8DCDD] rounded-sm p-6" data-testid="new-code-form">
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] mb-4">Create a new code</div>
              <div className="space-y-3">
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#6B5E62]">Code</label>
                  <input value={newCode} onChange={e => setNewCode(e.target.value.toUpperCase())}
                    data-testid="new-code-value" placeholder="ARMY-2026"
                    className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 bg-white text-sm font-mono uppercase mt-1"/>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-[#6B5E62]">Products this code unlocks (multi-select)</label>
                  <select multiple size={8} value={newCodeProducts}
                    onChange={e => setNewCodeProducts(Array.from(e.target.selectedOptions).map(o => o.value))}
                    data-testid="new-code-products"
                    className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 bg-white text-xs mt-1">
                    {products.filter(p => p.type === "course" || p.is_bundle).map(p => (
                      <option key={p.id} value={p.id}>{p.is_bundle ? "[BUNDLE] " : ""}{p.title.slice(0, 70)}</option>
                    ))}
                  </select>
                  <div className="text-[10px] text-[#6B5E62] mt-1">Hold ⌘/Ctrl to select multiple. Bundle codes unlock every course inside the bundle.</div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] tracking-[0.2em] uppercase text-[#6B5E62]">Max uses (blank = unlimited)</label>
                    <input type="number" min="1" value={newCodeMaxUses} onChange={e => setNewCodeMaxUses(e.target.value)}
                      data-testid="new-code-maxuses"
                      className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 bg-white text-sm mt-1"/>
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.2em] uppercase text-[#6B5E62]">Note (internal)</label>
                    <input value={newCodeNote} onChange={e => setNewCodeNote(e.target.value)}
                      data-testid="new-code-note" placeholder="Feb 2026 corporate seat licence"
                      className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 bg-white text-sm mt-1"/>
                  </div>
                </div>
                <button onClick={createCode} disabled={newCodeBusy}
                  data-testid="new-code-submit" className="btn-primary">
                  {newCodeBusy ? "Creating…" : "Create code"}
                </button>
              </div>
            </div>

            <div>
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] mb-3">Existing codes ({codes.length})</div>
              {codes.length === 0 ? (
                <div className="text-sm text-[#6B5E62] italic border border-dashed border-[#E8DCDD] rounded-sm p-6 text-center">No codes yet.</div>
              ) : (
                <div className="border border-[#E8DCDD] rounded-sm overflow-hidden bg-white">
                  <table className="w-full text-sm">
                    <thead className="bg-[#FAF4F5] text-[10px] tracking-[0.14em] uppercase text-[#8C4A57]">
                      <tr>
                        <th className="text-left px-4 py-3">Code</th>
                        <th className="text-left px-4 py-3">Unlocks</th>
                        <th className="text-left px-4 py-3">Uses</th>
                        <th className="text-left px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {codes.map(c => (
                        <tr key={c.code} data-testid={`code-row-${c.code}`} className="border-t border-[#E8DCDD]">
                          <td className="px-4 py-3">
                            <div className="font-mono text-xs text-[#2D2628]">{c.code}</div>
                            {c.note && <div className="text-[10px] text-[#6B5E62]">{c.note}</div>}
                          </td>
                          <td className="px-4 py-3 text-[11px] text-[#4A3E42]">
                            {(c.product_ids || []).length} product{(c.product_ids || []).length === 1 ? "" : "s"}
                          </td>
                          <td className="px-4 py-3 text-xs text-[#6B5E62]">
                            {c.uses || 0}{c.max_uses ? ` / ${c.max_uses}` : " · ∞"}
                          </td>
                          <td className="px-4 py-3">
                            {c.is_revoked ? (
                              <span className="text-[10px] uppercase tracking-widest text-[#B4573F]">Revoked</span>
                            ) : (
                              <button onClick={() => revokeCode(c.code)} data-testid={`code-revoke-${c.code}`}
                                className="text-[10px] uppercase tracking-widest text-[#8C4A57] hover:underline">Revoke</button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "audio" && (
        <div data-testid="admin-audio-tab">
          <p className="text-sm text-[#6B5E62] mb-6 max-w-3xl">
            Generate an MP3 narration for any ebook or course using ElevenLabs.
            The audio is attached to the product and appears on the buyer&apos;s
            <code className="mx-1 px-1 py-0.5 bg-[#FAF4F5] rounded text-[10px]">/payment/success</code>
            page as a downloadable MP3 alongside the PDF.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Generate TTS */}
            <div className="lg:col-span-2 bg-[#FAF4F5] border border-[#E8DCDD] rounded-sm p-6" data-testid="admin-tts-generate">
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] mb-4">
                Generate MP3 narration
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-[10px] tracking-[0.24em] uppercase text-[#6B5E62] mb-1 block">Product</label>
                  <select data-testid="tts-product-select" value={ttsProductId}
                    onChange={(e) => setTtsProductId(e.target.value)}
                    className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 bg-white">
                    {products.map((p) => (
                      <option key={p.id} value={p.id}>
                        [{p.type}] {p.title.slice(0, 65)}{p.title.length > 65 ? "…" : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.24em] uppercase text-[#6B5E62] mb-1 block">
                    Voice ({voices.length} available)
                  </label>
                  <select data-testid="tts-voice-select" value={ttsVoiceId}
                    onChange={(e) => setTtsVoiceId(e.target.value)}
                    className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 bg-white">
                    {voices.map((v) => (
                      <option key={v.voice_id} value={v.voice_id}>
                        {v.name}{v.voice_id === defaultVoiceId ? " ★" : ""}
                        {v.labels?.accent ? ` — ${v.labels.accent}` : ""}
                        {v.labels?.gender ? `, ${v.labels.gender}` : ""}
                      </option>
                    ))}
                  </select>
                  <div className="text-[10px] text-[#6B5E62] mt-1">★ = default (Adam)</div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-[10px] tracking-[0.24em] uppercase text-[#6B5E62]">
                      Script (max 10,000 chars)
                    </label>
                    <span className="text-[10px] text-[#6B5E62]">
                      {ttsText.length.toLocaleString()} / 10,000
                    </span>
                  </div>
                  <textarea data-testid="tts-text-input" value={ttsText}
                    onChange={(e) => setTtsText(e.target.value)} rows={10}
                    placeholder="Paste the ebook chapter, course intro, or teaser you want narrated…"
                    className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 bg-white text-sm focus:outline-none focus:border-[#8C4A57]" />
                </div>
                <button data-testid="tts-generate-btn" onClick={generateTTS} disabled={ttsBusy}
                  className="btn-primary">
                  <Music className="w-4 h-4 mr-2" />
                  {ttsBusy ? "Generating…" : "Generate & attach MP3"}
                </button>
              </div>
            </div>

            {/* Voice cloning */}
            <form onSubmit={submitClone} data-testid="admin-tts-clone"
              className="border border-[#E8DCDD] rounded-sm p-6">
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] mb-4">
                Clone a voice (IVC)
              </div>
              <p className="text-xs text-[#6B5E62] mb-4 leading-relaxed">
                Upload 1–3 clean audio samples of a single speaker (30–90 seconds each,
                MP3/WAV, no background music). ElevenLabs will create a new voice you can
                use with any generation above.
              </p>
              <label className="text-[10px] tracking-[0.24em] uppercase text-[#6B5E62] mb-1 block">Voice name</label>
              <input data-testid="clone-name-input" type="text" value={cloneName}
                onChange={(e) => setCloneName(e.target.value)} placeholder="Dr. Andrew Campbell"
                className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 bg-white mb-3 text-sm" />

              <label className="text-[10px] tracking-[0.24em] uppercase text-[#6B5E62] mb-1 block">Description (optional)</label>
              <input data-testid="clone-desc-input" type="text" value={cloneDesc}
                onChange={(e) => setCloneDesc(e.target.value)} placeholder="Warm mentor tone"
                className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 bg-white mb-3 text-sm" />

              <label className="text-[10px] tracking-[0.24em] uppercase text-[#6B5E62] mb-1 block">Audio samples (MP3/WAV)</label>
              <input id="clone-file-input" data-testid="clone-file-input" type="file"
                accept="audio/*" multiple
                onChange={(e) => setCloneFiles(Array.from(e.target.files || []))}
                className="w-full text-sm mb-4" />

              <button type="submit" disabled={cloneBusy} data-testid="clone-submit"
                className="btn-primary w-full justify-center">
                <Mic className="w-4 h-4 mr-2" />
                {cloneBusy ? "Uploading & cloning…" : "Clone voice"}
              </button>
            </form>
          </div>

          {/* Attached audio table */}
          <div className="mt-10">
            <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] mb-3">
              Attached audio ({audioAttachments.length})
            </div>
            {audioAttachments.length === 0 ? (
              <div className="text-sm text-[#6B5E62] italic border border-dashed border-[#E8DCDD] rounded-sm p-8 text-center">
                No audio narrations attached yet. Generate one above.
              </div>
            ) : (
              <div className="border border-[#E8DCDD] rounded-sm overflow-hidden bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-[#FAF4F5] text-[10px] tracking-[0.14em] uppercase text-[#8C4A57]">
                    <tr>
                      <th className="text-left px-4 py-3">Product</th>
                      <th className="text-left px-4 py-3">Filename</th>
                      <th className="text-left px-4 py-3">Voice</th>
                      <th className="text-left px-4 py-3">Size</th>
                      <th className="text-left px-4 py-3">Uploaded</th>
                      <th className="text-left px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {audioAttachments.map((a) => {
                      const prod = products.find((p) => p.id === a.product_id);
                      return (
                        <tr key={a.id} data-testid={`audio-row-${a.id}`} className="border-t border-[#E8DCDD]">
                          <td className="px-4 py-3">
                            <div className="text-[#2D2628] text-xs leading-tight">{prod?.title || a.product_id}</div>
                            <div className="text-[10px] text-[#6B5E62]">{a.product_id}</div>
                          </td>
                          <td className="px-4 py-3 text-xs text-[#4A3E42] truncate max-w-[220px]" title={a.original_filename}>
                            {a.original_filename}
                          </td>
                          <td className="px-4 py-3 text-xs text-[#4A3E42]">{a.voice_name || "—"}</td>
                          <td className="px-4 py-3 text-xs text-[#6B5E62]">{(a.size / 1024).toFixed(0)} KB</td>
                          <td className="px-4 py-3 text-xs text-[#6B5E62]">{new Date(a.uploaded_at).toLocaleString()}</td>
                          <td className="px-4 py-3">
                            <button data-testid={`audio-detach-${a.id}`} onClick={() => detach(a.id)}
                              className="text-[10px] uppercase tracking-widest text-[#8C4A57] hover:underline inline-flex items-center gap-1">
                              <Trash2 className="w-3 h-3" /> Detach
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "narrator" && (
        <div data-testid="admin-narrator-tab">
          <p className="text-sm text-[#6B5E62] mb-6 max-w-3xl">
            Paste your slide-deck speaker notes below. Separate each slide with a line
            containing only <code className="mx-1 px-1 py-0.5 bg-[#FAF4F5] rounded text-[10px]">---</code>.
            Optionally start any slide with a title on its own line prefixed with{" "}
            <code className="mx-1 px-1 py-0.5 bg-[#FAF4F5] rounded text-[10px]"># Slide title</code>.
            When you generate, you&apos;ll get back a ZIP containing one MP3 per slide plus{" "}
            <code className="mx-1 px-1 py-0.5 bg-[#FAF4F5] rounded text-[10px]">timing.csv</code>{" "}
            you can drop straight into your video editor.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 bg-[#FAF4F5] border border-[#E8DCDD] rounded-sm p-6" data-testid="narrator-form">
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] mb-4">
                Batch narration
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-[10px] tracking-[0.24em] uppercase text-[#6B5E62] mb-1 block">Deck name</label>
                  <input
                    type="text"
                    value={batchDeckName}
                    onChange={(e) => setBatchDeckName(e.target.value)}
                    data-testid="narrator-deck-name"
                    placeholder="module-2-emotional-intelligence"
                    className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 bg-white text-sm focus:outline-none focus:border-[#8C4A57]"
                  />
                </div>
                <div>
                  <label className="text-[10px] tracking-[0.24em] uppercase text-[#6B5E62] mb-1 block">
                    Voice ({voices.length} available)
                  </label>
                  <select
                    data-testid="narrator-voice-select"
                    value={batchVoiceId}
                    onChange={(e) => setBatchVoiceId(e.target.value)}
                    className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 bg-white text-sm"
                  >
                    <option value="">Default (Adam ★)</option>
                    {voices.map((v) => (
                      <option key={v.voice_id} value={v.voice_id}>
                        {v.name}{v.voice_id === defaultVoiceId ? " ★" : ""}
                        {v.labels?.accent ? ` — ${v.labels.accent}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] tracking-[0.24em] uppercase text-[#6B5E62]">
                  Speaker notes
                </label>
                <span className="text-[10px] text-[#6B5E62]" data-testid="narrator-slide-count">
                  {parseSlides(batchText).length} slide(s)
                </span>
              </div>
              <textarea
                data-testid="narrator-text-input"
                value={batchText}
                onChange={(e) => setBatchText(e.target.value)}
                rows={16}
                placeholder={`# Welcome slide\nHi and welcome to the module. Today we'll explore self-leadership...\n\n---\n\n# Why self-leadership matters\nBefore you can lead others, you must lead yourself. Research shows...`}
                className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 bg-white text-sm font-mono focus:outline-none focus:border-[#8C4A57]"
              />

              <div className="flex items-center gap-3 mt-4">
                <button
                  data-testid="narrator-generate-btn"
                  onClick={runBatchNarration}
                  disabled={batchBusy || !batchText.trim()}
                  className="btn-primary"
                >
                  <Presentation className="w-4 h-4 mr-2" />
                  {batchBusy ? "Generating…" : "Generate ZIP"}
                </button>
                {batchLastResult && (
                  <div className="text-xs text-[#6B5E62]" data-testid="narrator-result">
                    Last run: {batchLastResult.slides} slides · {batchLastResult.seconds.toFixed(1)}s total
                  </div>
                )}
              </div>
            </div>

            <div className="border border-[#E8DCDD] rounded-sm p-6">
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] mb-3">
                How it works
              </div>
              <ol className="text-xs text-[#6B5E62] space-y-3 leading-relaxed list-decimal pl-4">
                <li>Copy the speaker notes out of PowerPoint or Google Slides.</li>
                <li>Paste them into the box, one slide per block, separated by a line of <code className="px-1 py-0.5 bg-[#FAF4F5] rounded text-[10px]">---</code>.</li>
                <li>Optionally add a title with <code className="px-1 py-0.5 bg-[#FAF4F5] rounded text-[10px]"># ...</code> on the first line of a slide.</li>
                <li>Pick a voice (your cloned voice will appear here if you&apos;ve made one).</li>
                <li>Download the ZIP — each MP3 is named <code className="px-1 py-0.5 bg-[#FAF4F5] rounded text-[10px]">slide-001.mp3</code> and lined up with <code className="px-1 py-0.5 bg-[#FAF4F5] rounded text-[10px]">timing.csv</code>.</li>
              </ol>
              <div className="mt-6 text-[10px] tracking-[0.14em] uppercase text-[#8C4A57] font-semibold">Limits</div>
              <ul className="text-[11px] text-[#6B5E62] mt-2 space-y-1">
                <li>· Up to 200 slides per batch</li>
                <li>· Up to 10,000 characters per slide</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {tab === "analytics" && (
        <div data-testid="admin-analytics-tab">
          <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
            <div>
              <p className="text-sm text-[#6B5E62] max-w-2xl">
                Live funnel counts for the <code className="bg-[#FAF4F5] px-1 rounded text-[10px]">/character</code>{" "}
                enrollment flow. Numbers update every time you switch to this tab.
              </p>
              {analytics?.as_of && (
                <div className="text-[10px] tracking-[0.14em] uppercase text-[#6B5E62] mt-1">
                  As of {new Date(analytics.as_of).toLocaleString()}
                </div>
              )}
            </div>
            <button
              onClick={loadAnalytics}
              disabled={analyticsLoading}
              data-testid="analytics-refresh"
              className="text-[11px] tracking-[0.14em] uppercase font-semibold border border-[#8C4A57] text-[#8C4A57] px-4 py-2 rounded-sm hover:bg-[#8C4A57] hover:text-white transition disabled:opacity-60"
            >
              {analyticsLoading ? "Loading…" : "Refresh"}
            </button>
          </div>

          {!analytics && !analyticsLoading && (
            <div className="text-sm text-[#6B5E62]">No data yet. Click refresh above.</div>
          )}

          {adminSettings && (
            <div
              data-testid="admin-mega-toggle-card"
              className="mb-6 flex flex-wrap items-center justify-between gap-4 border border-[#E8DCDD] bg-white rounded-sm p-4"
            >
              <div>
                <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] mb-1">MEGA CRM sync</div>
                <div className="text-sm text-[#2D2628]">
                  {adminSettings.mega_configured ? (
                    <>Automatically streams /character leads and Character Code buyers into your MEGA CRM.</>
                  ) : (
                    <>MEGA CRM credentials not detected. Set <code className="text-[11px] bg-[#FAF4F5] px-1 rounded">MEGA_CRM_*</code> env vars to enable.</>
                  )}
                </div>
              </div>
              <button
                onClick={toggleMegaSync}
                disabled={!adminSettings.mega_configured}
                data-testid="admin-mega-toggle"
                aria-pressed={adminSettings.mega_sync_enabled}
                className={`relative inline-flex items-center h-8 w-16 rounded-full transition-colors ${
                  adminSettings.mega_sync_enabled ? "bg-[#8C4A57]" : "bg-[#E8DCDD]"
                } ${!adminSettings.mega_configured ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <span
                  className={`inline-block h-6 w-6 rounded-full bg-white shadow transition-transform ${
                    adminSettings.mega_sync_enabled ? "translate-x-9" : "translate-x-1"
                  }`}
                />
                <span className="sr-only">{adminSettings.mega_sync_enabled ? "On" : "Off"}</span>
              </button>
            </div>
          )}

          {analytics && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="analytics-cards">
              {[
                { key: "enrolls",          label: "/character email captures", accent: "#8C4A57" },
                { key: "paid_character",   label: "Character Code purchases", accent: "#8C4A57" },
                { key: "welcome_sent",     label: "Welcome emails sent",      accent: "#4A3E42" },
                { key: "magic_redeemed",   label: "One-click sign-ins used",  accent: "#4A3E42" },
                { key: "nudges_sent",      label: "24h abandon nudges sent",  accent: "#C9A961" },
                { key: "nudges_cancelled", label: "Nudges cancelled by pay",  accent: "#C9A961" },
                { key: "paid_all",         label: "All paid transactions",    accent: "#2D2628" },
              ].map((m) => {
                const v = analytics[m.key] || { total: 0, last_7d: 0, last_24h: 0 };
                return (
                  <div
                    key={m.key}
                    data-testid={`analytics-card-${m.key}`}
                    className="border border-[#E8DCDD] bg-white rounded-sm p-5"
                  >
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[#6B5E62] mb-2">
                      {m.label}
                    </div>
                    <div className="font-serif text-4xl mb-3" style={{ color: m.accent }}>
                      {v.total}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="border-t border-[#E8DCDD] pt-2">
                        <div className="text-[9px] tracking-widest uppercase text-[#6B5E62]">Last 7 d</div>
                        <div className="text-[#2D2628] font-semibold">{v.last_7d}</div>
                      </div>
                      <div className="border-t border-[#E8DCDD] pt-2">
                        <div className="text-[9px] tracking-widest uppercase text-[#6B5E62]">Last 24 h</div>
                        <div className="text-[#2D2628] font-semibold">{v.last_24h}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {tab === "certificate" && (
        <div data-testid="admin-certificate-tab">
          <p className="text-sm text-[#6B5E62] mb-6 max-w-2xl">
            Upload Dr. Campbell&apos;s signature image (transparent PNG works best). It will be
            embedded on every Certificate of Completion generated for course purchases. Both the
            Global Leader Solutions globe logo and the Training Institute seal are already applied.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <form onSubmit={uploadSignature} data-testid="admin-signature-form"
              className="bg-[#FAF4F5] border border-[#E8DCDD] rounded-sm p-6">
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] mb-3">
                Upload signature
              </div>
              <label className="text-xs text-[#6B5E62] mb-2 block">
                PNG or JPG · max 5 MB · transparent background recommended
              </label>
              <input id="admin-sig-input" data-testid="admin-sig-input" type="file"
                accept="image/png,image/jpeg"
                onChange={(e) => setSigFile(e.target.files?.[0] || null)}
                className="w-full text-sm mb-4" />
              <button type="submit" disabled={sigUploading}
                data-testid="admin-signature-submit" className="btn-primary">
                <Upload className="w-4 h-4 mr-2" />
                {sigUploading ? "Uploading…" : signature ? "Replace signature" : "Install signature"}
              </button>
            </form>

            <div className="border border-[#E8DCDD] rounded-sm p-6" data-testid="admin-signature-status">
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] mb-3">
                Current status
              </div>
              {signature ? (
                <>
                  <div className="text-sm text-[#2D2628] mb-1 truncate" title={signature.original_filename}>
                    {signature.original_filename}
                  </div>
                  <div className="text-xs text-[#6B5E62] mb-4">
                    {(signature.size / 1024).toFixed(0)} KB · uploaded{" "}
                    {new Date(signature.uploaded_at).toLocaleString()}
                  </div>
                  <div className="text-xs text-[#8C4A57]">
                    Active and embedded on every new certificate.
                  </div>
                </>
              ) : (
                <div className="text-sm text-[#6B5E62] italic">
                  No signature installed yet. Certificates will be generated without a signature
                  until you upload one.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "quizzes" && (
        <div data-testid="admin-quizzes-tab">
          <p className="text-sm text-[#6B5E62] mb-6 max-w-2xl">
            Configure the end-of-course quiz for each course. Buyers can only take the quiz
            after purchasing. When they score ≥ passing_score, we email their signed
            Certificate of Completion automatically.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Course picker */}
            <div className="lg:col-span-4 border border-[#E8DCDD] rounded-sm bg-white">
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] px-4 py-3 border-b border-[#E8DCDD] bg-[#FAF4F5]">
                Courses ({courseProducts.length})
              </div>
              <ul>
                {courseProducts.map(p => {
                  const hasQuiz = quizzes.some(q => q.product_id === p.id);
                  return (
                    <li key={p.id} data-testid={`admin-quiz-course-${p.id}`}
                      className={`px-4 py-3 border-b border-[#E8DCDD] cursor-pointer transition-colors ${
                        quizProductId === p.id ? "bg-[#FAF4F5]" : "hover:bg-[#FAF4F5]/50"}`}
                      onClick={() => loadQuizForProduct(p.id)}>
                      <div className="text-sm text-[#2D2628] leading-tight">{p.title}</div>
                      <div className="flex items-center justify-between mt-1">
                        <div className="text-[10px] tracking-[0.14em] uppercase text-[#8C4A57]">
                          {hasQuiz ? "✓ Quiz configured" : "No quiz yet"}
                        </div>
                        {hasQuiz && (
                          <button data-testid={`admin-quiz-delete-${p.id}`}
                            onClick={(e) => { e.stopPropagation(); deleteQuiz(p.id); }}
                            className="text-xs text-red-600 hover:underline">Delete</button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Editor */}
            <div className="lg:col-span-8">
              {!quizProductId ? (
                <div className="border border-dashed border-[#E8DCDD] rounded-sm p-8 text-center text-sm text-[#6B5E62]">
                  Pick a course on the left to view / edit its quiz.
                </div>
              ) : (
                <div>
                  <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] mb-2">
                    Editing course <strong>{quizProductId}</strong>
                  </div>

                  {/* Course modules editor */}
                  <div className="bg-white border border-[#E8DCDD] rounded-sm p-5 mb-6" data-testid="course-modules-editor">
                    <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] mb-2">Course modules ({modules.length})</div>
                    <p className="text-xs text-[#6B5E62] mb-4 leading-relaxed">
                      Break this course into ordered modules. Each module can have its own title,
                      description, and attached files. Students see them in order on their /payment/success page.
                    </p>
                    <div className="flex gap-2 mb-5">
                      <input value={newModuleTitle} onChange={e => setNewModuleTitle(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && createModule()}
                        data-testid="new-module-title" placeholder="Module title (e.g. Introduction)"
                        className="flex-1 border border-[#E8DCDD] rounded-sm px-3 py-2 text-sm"/>
                      <button onClick={createModule} data-testid="new-module-submit" className="btn-primary text-xs">+ Add module</button>
                    </div>
                    {modules.length === 0 && (
                      <div className="text-xs text-[#6B5E62] italic bg-[#FAF4F5] border border-dashed border-[#E8DCDD] rounded-sm p-4">No modules yet — add the first one above.</div>
                    )}
                    <div className="space-y-3">
                      {modules.map((m, i) => (
                        <div key={m.id} data-testid={`admin-module-${m.id}`} className="border border-[#E8DCDD] rounded-sm p-4">
                          <div className="flex items-start gap-3 mb-3">
                            <div className="flex flex-col gap-0.5">
                              <button disabled={i === 0} onClick={() => moveModule(i, -1)} className="w-7 h-7 border border-[#E8DCDD] rounded text-xs hover:bg-[#FAF4F5] disabled:opacity-30" title="Move up">▲</button>
                              <button disabled={i === modules.length - 1} onClick={() => moveModule(i, 1)} className="w-7 h-7 border border-[#E8DCDD] rounded text-xs hover:bg-[#FAF4F5] disabled:opacity-30" title="Move down">▼</button>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-[10px] tracking-[0.2em] uppercase text-[#8C4A57] mb-1">Module {i + 1}</div>
                              <input value={m.title} onChange={e => setModules(modules.map(x => x.id === m.id ? {...x, title: e.target.value} : x))}
                                onBlur={() => updateModule(m, { title: m.title })}
                                className="w-full font-serif text-lg text-[#2D2628] border-b border-[#E8DCDD] focus:outline-none focus:border-[#8C4A57] mb-2 pb-1"/>
                              <textarea value={m.description || ""} onChange={e => setModules(modules.map(x => x.id === m.id ? {...x, description: e.target.value} : x))}
                                onBlur={() => updateModule(m, { description: m.description || null })}
                                rows={2} placeholder="Short description (optional)"
                                className="w-full text-xs text-[#4A3E42] border border-[#E8DCDD] rounded-sm px-2 py-1.5"/>
                              <input type="url" value={m.video_url || ""}
                                onChange={e => setModules(modules.map(x => x.id === m.id ? {...x, video_url: e.target.value} : x))}
                                onBlur={() => updateModule(m, { video_url: (m.video_url || "").trim() || null })}
                                data-testid={`admin-module-video-${m.id}`}
                                placeholder="YouTube URL (optional) — e.g. https://youtu.be/xxxxxxxxxxx"
                                className="w-full text-xs text-[#4A3E42] border border-[#E8DCDD] rounded-sm px-2 py-1.5 mt-2"/>
                            </div>
                            <button onClick={() => deleteModule(m)} className="text-[10px] uppercase tracking-widest text-[#B4573F] hover:underline">Delete</button>
                          </div>
                          {/* Attached files */}
                          {m.files && m.files.length > 0 && (
                            <div className="mt-3 mb-3 space-y-1">
                              {m.files.map(f => (
                                <div key={f.id} className="text-xs text-[#4A3E42] flex justify-between items-center bg-[#FAF4F5] rounded-sm px-3 py-1.5">
                                  <span className="truncate"><span className="text-[10px] uppercase tracking-widest text-[#8C4A57] mr-2">{f.kind}</span>{f.filename}</span>
                                  <span className="text-[10px] text-[#6B5E62]">{Math.round((f.size || 0) / 1024)} KB</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {/* Upload buttons */}
                          <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-[#E8DCDD]">
                            {["ebook", "workbook", "supplement"].map(k => (
                              <label key={k} className="text-[10px] tracking-widest uppercase text-[#8C4A57] border border-[#E8DCDD] rounded-sm px-3 py-1.5 cursor-pointer hover:bg-[#FAF4F5]">
                                + {k} PDF
                                <input type="file" accept="application/pdf" className="hidden"
                                  disabled={uploadingModuleId === m.id}
                                  onChange={e => { uploadToModule(m.id, k, e.target.files[0]); e.target.value = ""; }}/>
                              </label>
                            ))}
                            {uploadingModuleId === m.id && <span className="text-[11px] text-[#8C4A57]">Uploading…</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Course page content editor */}
                  <div className="bg-white border border-[#E8DCDD] rounded-sm p-5 mb-6" data-testid="course-page-editor">
                    <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] mb-2">
                      Course page content
                    </div>
                    <p className="text-xs text-[#6B5E62] mb-4 leading-relaxed">
                      Edit the copy that appears on <code>/courses/{quizProductId}</code>. For list fields
                      (learning objectives, who it&apos;s for, why choose) put one item per line. Blank fields
                      hide their section.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-[10px] tracking-[0.2em] uppercase text-[#6B5E62]">Length (hours)</label>
                        <input type="number" step="0.5" min="0" data-testid="page-length" value={pageLengthHours}
                          onChange={e => setPageLengthHours(e.target.value)}
                          className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 text-sm mt-1"/>
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-[10px] tracking-[0.2em] uppercase text-[#6B5E62]">Course narrative (about this course)</label>
                        <textarea data-testid="page-narrative" value={pageNarrative} onChange={e => setPageNarrative(e.target.value)} rows={4}
                          className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 text-sm mt-1"/>
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-[10px] tracking-[0.2em] uppercase text-[#6B5E62]">Course description</label>
                        <textarea data-testid="page-description" value={pageDescription} onChange={e => setPageDescription(e.target.value)} rows={3}
                          className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 text-sm mt-1"/>
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-[10px] tracking-[0.2em] uppercase text-[#6B5E62]">Learning objectives (one per line)</label>
                        <textarea data-testid="page-objectives" value={pageObjectives} onChange={e => setPageObjectives(e.target.value)} rows={5}
                          className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 text-sm mt-1"/>
                      </div>
                      <div className="md:col-span-2">
                        <label className="text-[10px] tracking-[0.2em] uppercase text-[#6B5E62]">Why take this course</label>
                        <textarea data-testid="page-why-take" value={pageWhyTake} onChange={e => setPageWhyTake(e.target.value)} rows={3}
                          className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 text-sm mt-1"/>
                      </div>
                      <div>
                        <label className="text-[10px] tracking-[0.2em] uppercase text-[#6B5E62]">Who this is for (one per line)</label>
                        <textarea data-testid="page-who-for" value={pageWhoFor} onChange={e => setPageWhoFor(e.target.value)} rows={4}
                          className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 text-sm mt-1"/>
                      </div>
                      <div>
                        <label className="text-[10px] tracking-[0.2em] uppercase text-[#6B5E62]">Why choose this course (one per line)</label>
                        <textarea data-testid="page-why-choose" value={pageWhyChoose} onChange={e => setPageWhyChoose(e.target.value)} rows={4}
                          className="w-full border border-[#E8DCDD] rounded-sm px-3 py-2 text-sm mt-1"/>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center gap-3">
                      <button onClick={savePageContent} disabled={pageSaving}
                        data-testid="page-save"
                        className="btn-primary">
                        {pageSaving ? "Saving…" : "Save course page"}
                      </button>
                      <a href={`/courses/${quizProductId}`} target="_blank" rel="noopener noreferrer"
                        className="text-[11px] uppercase tracking-widest text-[#8C4A57] hover:underline">
                        Preview page →
                      </a>
                    </div>
                    <p className="text-[10px] text-[#6B5E62] mt-3 italic">
                      Workbook, ebook, audio, and certificate inclusions on the course page are detected automatically from files attached in the Files tab — no manual toggles needed.
                    </p>
                  </div>

                  {/* Video embed URL */}
                  <div className="bg-white border border-[#E8DCDD] rounded-sm p-5 mb-6" data-testid="course-video-editor">
                    <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] mb-2">
                      Course video
                    </div>
                    <p className="text-xs text-[#6B5E62] mb-3 leading-relaxed">
                      Paste a YouTube, Vimeo, or direct <strong>MP4</strong> link — accepted formats:
                      <code className="mx-1 px-1 py-0.5 bg-[#FAF4F5] rounded text-[10px]">youtube.com/watch?v=…</code>,
                      <code className="mx-1 px-1 py-0.5 bg-[#FAF4F5] rounded text-[10px]">youtu.be/…</code>,
                      <code className="mx-1 px-1 py-0.5 bg-[#FAF4F5] rounded text-[10px]">vimeo.com/…</code>,
                      <code className="mx-1 px-1 py-0.5 bg-[#FAF4F5] rounded text-[10px]">https://…/video.mp4</code>
                      &nbsp;— we render it above the course description AND on the buyer&apos;s /payment/success page.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input
                        data-testid="course-video-url-input"
                        type="url"
                        value={videoUrl}
                        onChange={(e) => setVideoUrl(e.target.value)}
                        placeholder="https://www.youtube.com/watch?v=…"
                        className="flex-1 border border-[#E8DCDD] rounded-sm px-3 py-2 bg-white text-sm focus:outline-none focus:border-[#8C4A57]"
                      />
                      <button
                        data-testid="course-video-url-save"
                        onClick={saveVideoUrl}
                        disabled={videoSaving}
                        className="btn-primary"
                      >
                        {videoSaving ? "Saving…" : videoUrl.trim() ? "Save URL" : "Clear URL"}
                      </button>
                    </div>
                    {courseSettings[quizProductId]?.video_embed_url && (
                      <div className="text-[10px] text-[#6B5E62] mt-2" data-testid="course-video-current">
                        Current: <a className="text-[#8C4A57] hover:underline" href={courseSettings[quizProductId].video_embed_url} target="_blank" rel="noopener noreferrer">
                          {courseSettings[quizProductId].video_embed_url}
                        </a>
                      </div>
                    )}
                  </div>

                  <div className="text-[10px] tracking-[0.24em] uppercase text-[#8C4A57] mb-2">
                    Quiz for <strong>{quizProductId}</strong>
                  </div>
                  <p className="text-xs text-[#6B5E62] mb-3 leading-relaxed">
                    Edit the JSON below. <code>correct_answer</code> is the option index
                    (<code>&quot;0&quot;</code>, <code>&quot;1&quot;</code>, …) for multiple_choice, or one or more
                    pipe-separated keywords (case-insensitive substring match) for short_answer.
                  </p>
                  <textarea value={quizJson} onChange={(e) => setQuizJson(e.target.value)}
                    data-testid="admin-quiz-json"
                    rows={22}
                    className="w-full font-mono text-xs border border-[#E8DCDD] rounded-sm p-3 bg-white focus:outline-none focus:border-[#8C4A57]" />
                  <div className="mt-3 flex gap-3">
                    <button onClick={saveQuiz} disabled={quizSaving}
                      data-testid="admin-quiz-save" className="btn-primary">
                      <Upload className="w-4 h-4 mr-2" />
                      {quizSaving ? "Saving…" : "Save quiz"}
                    </button>
                    <button onClick={() => loadQuizForProduct(quizProductId)}
                      className="px-4 py-2 border border-[#E8DCDD] rounded-sm text-sm">
                      Reset
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "completions" && (
        <div data-testid="admin-completions-tab">
          <p className="text-sm text-[#6B5E62] mb-6 max-w-2xl">
            Every course completion attempt — passed or failed — is logged here. Passed
            completions automatically trigger a certificate email via Resend.
          </p>
          {completions.length === 0 ? (
            <div className="text-sm text-[#6B5E62] italic border border-dashed border-[#E8DCDD] rounded-sm p-8 text-center">
              No completions yet.
            </div>
          ) : (
            <div className="border border-[#E8DCDD] rounded-sm overflow-hidden bg-white">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF4F5] text-[10px] tracking-[0.14em] uppercase text-[#8C4A57]">
                  <tr>
                    <th className="text-left px-4 py-3">Learner</th>
                    <th className="text-left px-4 py-3">Course</th>
                    <th className="text-left px-4 py-3">Score</th>
                    <th className="text-left px-4 py-3">Certificate</th>
                    <th className="text-left px-4 py-3">Emailed</th>
                    <th className="text-left px-4 py-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {completions.map(c => (
                    <tr key={c.id} data-testid={`admin-completion-${c.id}`} className="border-t border-[#E8DCDD]">
                      <td className="px-4 py-3">
                        <div className="text-[#2D2628]">{c.learner_name}</div>
                        <div className="text-xs text-[#6B5E62]">{c.learner_email || "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-[#4A3E42]">{c.product_title}</td>
                      <td className={`px-4 py-3 font-semibold ${c.passed ? "text-green-700" : "text-red-700"}`}>
                        {c.percentage}% {c.passed ? "✓" : "✗"}
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-[#4A3E42]">{c.certificate_id || "—"}</td>
                      <td className="px-4 py-3">{c.certificate_emailed ? "✓" : "—"}</td>
                      <td className="px-4 py-3 text-xs text-[#6B5E62]">
                        {new Date(c.completed_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "drips" && (
        <div data-testid="admin-drips-tab">
          <p className="text-sm text-[#6B5E62] mb-4 max-w-2xl">
            Every new subscriber is auto-queued for a 2-email nurture drip (Day 2: Character
            Code pitch, Day 5: LEADER20 last-call). A background worker sends any due mail every
            5 minutes. Unsubscribing cancels all pending drip mail for that address.
          </p>
          <div className="mb-4 flex gap-2 flex-wrap items-center">
            <button data-testid="drips-run-now"
              onClick={async () => {
                try {
                  await axios.post(`${API}/admin/drips/tick`, {}, authHeaders(token));
                  toast.success("Worker triggered — refreshing…");
                  await loadAll(token);
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Trigger failed");
                }
              }}
              className="btn-primary">
              <Mail className="w-4 h-4 mr-2" />
              Run drip worker now
            </button>
            <div className="text-xs text-[#6B5E62] ml-3">
              Pending: <strong>{drips.filter(d => !d.sent && !d.cancelled).length}</strong> ·
              &nbsp;Sent: <strong>{drips.filter(d => d.sent).length}</strong> ·
              &nbsp;Cancelled: <strong>{drips.filter(d => d.cancelled).length}</strong>
            </div>
          </div>
          {drips.length === 0 ? (
            <div className="text-sm text-[#6B5E62] italic border border-dashed border-[#E8DCDD] rounded-sm p-8 text-center">
              No drip jobs queued yet.
            </div>
          ) : (
            <div className="border border-[#E8DCDD] rounded-sm overflow-hidden bg-white">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF4F5] text-[10px] tracking-[0.14em] uppercase text-[#8C4A57]">
                  <tr>
                    <th className="text-left px-4 py-3">Recipient</th>
                    <th className="text-left px-4 py-3">Step</th>
                    <th className="text-left px-4 py-3">Scheduled for</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Message ID</th>
                  </tr>
                </thead>
                <tbody>
                  {drips.map(d => {
                    const status = d.cancelled ? "cancelled" : d.sent ? "sent" : "pending";
                    const cls = status === "sent" ? "text-green-700"
                              : status === "cancelled" ? "text-red-700" : "text-[#8C4A57]";
                    return (
                      <tr key={d.id} data-testid={`drip-${d.id}`} className="border-t border-[#E8DCDD]">
                        <td className="px-4 py-3">
                          <div className="text-[#2D2628]">{d.email}</div>
                          {d.name && <div className="text-xs text-[#6B5E62]">{d.name}</div>}
                        </td>
                        <td className="px-4 py-3 text-xs font-mono uppercase text-[#4A3E42]">{d.step}</td>
                        <td className="px-4 py-3 text-xs text-[#6B5E62]">
                          {new Date(d.scheduled_for).toLocaleString()}
                        </td>
                        <td className={`px-4 py-3 font-semibold text-xs uppercase tracking-widest ${cls}`}>
                          {status}
                        </td>
                        <td className="px-4 py-3 text-xs font-mono text-[#4A3E42]">
                          {d.message_id ? d.message_id.slice(0, 16) + "…" : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === "newsletter" && (
        <div data-testid="admin-newsletter-table">
          <p className="text-sm text-[#6B5E62] mb-6">
            Everyone who submitted their email through the lead-magnet form, newsletter footer, or
            exit-intent modal. Sorted by most recent.
          </p>
          {signups.length === 0 ? (
            <div className="bg-[#FAF4F5] border border-[#E8DCDD] rounded-sm p-10 text-center text-[#6B5E62]">
              No signups yet.
            </div>
          ) : (
            <div className="border border-[#E8DCDD] rounded-sm overflow-hidden">
              <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-[#FAF4F5] text-[10px] tracking-[0.24em] uppercase text-[#6B5E62]">
                <div className="col-span-5">Email</div>
                <div className="col-span-4">Name</div>
                <div className="col-span-3">Signed up</div>
              </div>
              {signups.map((s) => (
                <div key={s.id || s.email} className="grid grid-cols-12 gap-4 px-4 py-3 border-t border-[#F1E7E9] items-center text-sm">
                  <div className="col-span-5 text-[#2D2628] truncate" data-testid={`signup-email-${s.email}`}>
                    <a href={`mailto:${s.email}`} className="hover:text-[#8C4A57] hover:underline">
                      {s.email}
                    </a>
                  </div>
                  <div className="col-span-4 text-[#6B5E62] truncate">{s.name || <span className="italic text-[#B79CA1]">—</span>}</div>
                  <div className="col-span-3 text-[#6B5E62] text-xs">
                    {new Date(s.created_at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
          {signups.length > 0 && (
            <div className="mt-4 flex justify-end">
              <button
                data-testid="signups-export-csv"
                onClick={() => {
                  const rows = [["email", "name", "created_at"]]
                    .concat(signups.map((s) => [s.email, s.name || "", s.created_at || ""]));
                  const csv = rows.map((r) => r.map((c) => `"${(c || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
                  const blob = new Blob([csv], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url; a.download = `newsletter-signups-${new Date().toISOString().slice(0,10)}.csv`; a.click();
                  URL.revokeObjectURL(url);
                }}
                className="btn-outline text-xs"
              >
                Export CSV
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
